import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import SecurityIcon from '@mui/icons-material/Security';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import * as React from 'react';

const PolicySection = ({ title, icon, children }) => (
  <Card elevation={1} sx={{ mb: 3 }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {icon}
        <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      {children}
    </CardContent>
  </Card>
);

const PolicyText = ({ children }) => (
  <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6, color: 'text.secondary' }}>
    {children}
  </Typography>
);

const PolicyList = ({ items }) => (
  <Box component="ul" sx={{ pl: 2, mb: 2 }}>
    {items.map((item, index) => (
      <Box component="li" key={index} sx={{ mb: 1 }}>
        <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
          {item}
        </Typography>
      </Box>
    ))}
  </Box>
);

export default function Index() {
  const lastUpdated = new Date().toLocaleDateString('pt-BR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Política de Privacidade e Termos de Uso
        </Typography>
        <Typography variant="h6" color="text.secondary">
          DriveSync - Plataforma de Gestão Logística B2B
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Última atualização: {lastUpdated}
        </Typography>
      </Box>

      {/* Introduction */}
      <PolicySection title="1. Introdução" icon={<SecurityIcon color="primary" />}>
        <PolicyText>
          A <strong>DriveSync</strong> é uma plataforma voltada exclusivamente para o mercado B2B,
          oferecendo soluções de gestão logística e acompanhamento de entregas. Esta política
          descreve como coletamos, usamos e protegemos as informações em nossos serviços.
        </PolicyText>
        <PolicyText>
          Ao utilizar nossa plataforma e aplicativo móvel, você concorda com os termos
          descritos nesta política. É importante que você leia atentamente todas as seções.
        </PolicyText>
      </PolicySection>

      {/* Data Collection */}
      <PolicySection title="2. Coleta de Dados" icon={<PhoneIphoneIcon color="primary" />}>
        <PolicyText>
          <strong>Dados Empresariais:</strong> Coletamos informações necessárias para o
          funcionamento da plataforma, incluindo dados da empresa, usuários autorizados,
          e informações relacionadas às operações logísticas.
        </PolicyText>

        <PolicyText>
          <strong>Dados de Localização:</strong> Nosso aplicativo móvel coleta dados de
          localização precisa em tempo real durante o acompanhamento das viagens, incluindo:
        </PolicyText>

        <PolicyList items={[
          'Localização GPS precisa durante a execução de rotas de entrega',
          'Dados de localização em segundo plano durante o período de trabalho',
          'Informações de velocidade e direção para otimização de rotas',
          'Dados de paradas e tempos de permanência em locais específicos'
        ]} />

        <PolicyText>
          <strong>Dados Operacionais:</strong> Registramos informações sobre viagens,
          entregas, fotos de comprovação, horários e outras informações necessárias
          para o acompanhamento das operações.
        </PolicyText>
      </PolicySection>

      {/* Location Data Usage */}
      <PolicySection title="3. Uso de Dados de Localização" icon={<LocationOnIcon color="primary" />}>
        <PolicyText>
          Os dados de localização são utilizados exclusivamente para:
        </PolicyText>

        <PolicyList items={[
          'Rastreamento em tempo real de veículos e motoristas durante as entregas',
          'Otimização de rotas e cálculo de tempos de viagem',
          'Geração de relatórios de produtividade e eficiência operacional',
          'Garantia de segurança e monitoramento de emergências',
          'Verificação de cumprimento de rotas e horários estabelecidos'
        ]} />

        <PolicyText>
          <strong>Coleta em Segundo Plano:</strong> O aplicativo pode coletar dados de
          localização mesmo quando não está em uso ativo, durante o período de trabalho
          do motorista, para garantir o acompanhamento contínuo das operações.
        </PolicyText>
      </PolicySection>

      {/* Data Sharing */}
      <PolicySection title="4. Compartilhamento de Dados" icon={<SecurityIcon color="primary" />}>
        <PolicyText>
          Os dados coletados são compartilhados apenas com:
        </PolicyText>

        <PolicyList items={[
          'Empresas clientes que contrataram os serviços de entrega',
          'Transportadoras parceiras envolvidas nas operações',
          'Prestadores de serviços técnicos essenciais para o funcionamento da plataforma',
          'Autoridades competentes, quando exigido por lei ou ordem judicial'
        ]} />

        <PolicyText>
          <strong>Não vendemos, alugamos ou compartilhamos dados pessoais com terceiros
            para fins comerciais, publicitários ou de marketing.</strong>
        </PolicyText>
      </PolicySection>

      {/* Data Security */}
      <PolicySection title="5. Segurança dos Dados" icon={<SecurityIcon color="primary" />}>
        <PolicyText>
          Implementamos medidas de segurança técnicas e organizacionais para proteger
          os dados coletados:
        </PolicyText>

        <PolicyList items={[
          'Criptografia de dados em trânsito e em repouso',
          'Controle de acesso restrito baseado em perfis de usuário',
          'Monitoramento contínuo de segurança e detecção de ameaças',
          'Backups regulares e planos de recuperação de desastres',
          'Treinamento regular da equipe sobre práticas de segurança'
        ]} />
      </PolicySection>

      {/* User Rights */}
      <PolicySection title="6. Direitos dos Usuários" icon={<SecurityIcon color="primary" />}>
        <PolicyText>
          De acordo com a Lei Geral de Proteção de Dados (LGPD), você tem direito a:
        </PolicyText>

        <PolicyList items={[
          'Solicitar informações sobre quais dados pessoais são tratados',
          'Solicitar correção de dados incompletos, inexatos ou desatualizados',
          'Solicitar eliminação de dados pessoais desnecessários ou excessivos',
          'Solicitar portabilidade de dados para outro fornecedor',
          'Revogar consentimento para tratamento de dados'
        ]} />

        <PolicyText>
          Para exercer seus direitos, entre em contato conosco através dos canais
          oficiais de atendimento.
        </PolicyText>
      </PolicySection>

      {/* Cookies and Technology */}
      <PolicySection title="7. Cookies e Tecnologias" icon={<PhoneIphoneIcon color="primary" />}>
        <PolicyText>
          Utilizamos cookies e tecnologias similares para:
        </PolicyText>

        <PolicyList items={[
          'Manter sessões de usuário autenticadas',
          'Lembrar preferências de configuração',
          'Melhorar a experiência de uso da plataforma',
          'Coletar dados analíticos sobre uso do sistema'
        ]} />

        <PolicyText>
          Você pode configurar seu navegador para bloquear cookies, mas isso pode
          afetar o funcionamento de algumas funcionalidades da plataforma.
        </PolicyText>
      </PolicySection>

      {/* Changes to Policy */}
      <PolicySection title="8. Alterações nesta Política" icon={<SecurityIcon color="primary" />}>
        <PolicyText>
          A DriveSync pode atualizar esta Política de Privacidade para refletir mudanças
          nos serviços ou para atender a exigências legais. Alterações significativas
          serão comunicadas com antecedência mínima de 30 dias.
        </PolicyText>

        <PolicyText>
          Recomendamos que você revise periodicamente esta política para se manter
          informado sobre como protegemos suas informações.
        </PolicyText>
      </PolicySection>

      {/* Contact */}
      <PolicySection title="9. Contato" icon={<EmailIcon color="primary" />}>
        <PolicyText>
          Para dúvidas sobre esta Política de Privacidade, proteção de dados ou
          exercício de direitos, entre em contato:
        </PolicyText>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>E-mail:</strong>{' '}
            <Link href="mailto:privacidade@drivesync.com" color="primary">
              privacidade@drivesync.com
            </Link>
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Suporte Geral:</strong>{' '}
            <Link href="mailto:contato@drivesync.com" color="primary">
              contato@drivesync.com
            </Link>
          </Typography>
        </Box>
      </PolicySection>

      <Divider sx={{ my: 4 }} />

      {/* Footer */}
      <Box sx={{ textAlign: 'center', py: 3, backgroundColor: 'grey.50', borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary">
          © 2025 DriveSync. Todos os direitos reservados.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Plataforma de Gestão Logística B2B - Comprometida com a proteção de dados
        </Typography>
      </Box>
    </Container>
  );
} 